import { useEffect, useRef, useState, useCallback } from "react";
import {
  rtdb,
  ref,
  set,
  update,
  remove,
  onValue,
  onDisconnect,
} from "../firebase";
import { v4 as uuidv4 } from "uuid";

const PRESENCE_PATH = "swapPresence";

const formatTimeAgo = (minutesAgo) => {
  if (minutesAgo < 1) return "Just now";
  if (minutesAgo === 1) return "1 min ago";
  if (minutesAgo < 60) return `${minutesAgo} mins ago`;
  const hours = Math.floor(minutesAgo / 60);
  return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
};

export default function useSwapSession({ deviceId, userEmail }) {
  const sessionId = useRef(uuidv4()).current;
  const sessionCreated = useRef(false);
  const deviceIdRef = useRef(deviceId);
  const userEmailRef = useRef(userEmail);

  const [allSessions, setAllSessions] = useState([]);
  const [otherSessions, setOtherSessions] = useState([]);

  useEffect(() => {
    deviceIdRef.current = deviceId;
  }, [deviceId]);

  useEffect(() => {
    userEmailRef.current = userEmail;
  }, [userEmail]);

  const buildSessionRef = useCallback(
    (did, sid) => {
      if (!did) return null;
      return ref(rtdb, `${PRESENCE_PATH}/${did}/${sid || sessionId}`);
    },
    [sessionId],
  );

  const parseSnapshot = useCallback(
    (snapshot) => {
      if (!snapshot.exists()) {
        return { all: [], others: [] };
      }

      const data = snapshot.val();
      const now = Date.now();
      const all = [];
      const others = [];

      Object.entries(data).forEach(([sid, session]) => {
        if (!session || !session.userEmail) return;

        const startedAt = session.startedAt || 0;
        const minutesAgo = Math.floor((now - startedAt) / 60000);
        const isOwn = sid === sessionId;

        const parsed = {
          sessionId: sid,
          userEmail: session.userEmail,
          driverId: session.driverId,
          partnerId: session.partnerId,
          driverName: session.driverName || session.driverId,
          step: session.step,
          stepLabel: session.stepLabel,
          doorNumber: session.doorNumber ?? null,
          insertedBatteryId: session.insertedBatteryId || null,
          takenBatteryId: session.takenBatteryId || null,
          startedAt: startedAt ? new Date(startedAt) : null,
          minutesAgo,
          timeAgoLabel: formatTimeAgo(minutesAgo),
          isOwnSession: isOwn,
        };

        all.push(parsed);
        if (!isOwn) others.push(parsed);
      });

      const sortFn = (a, b) =>
        (b.startedAt?.getTime() || 0) - (a.startedAt?.getTime() || 0);

      all.sort(sortFn);
      others.sort(sortFn);

      return { all, others };
    },
    [sessionId],
  );

  useEffect(() => {
    if (!deviceId) {
      setAllSessions([]);
      setOtherSessions([]);
      return;
    }

    const deviceRef = ref(rtdb, `${PRESENCE_PATH}/${deviceId}`);

    const unsub = onValue(
      deviceRef,
      (snapshot) => {
        const { all, others } = parseSnapshot(snapshot);
        setAllSessions(all);
        setOtherSessions(others);
      },
      (error) => {
        console.error("[useSwapSession] Listener error:", error);
      },
    );

    return () => unsub();
  }, [deviceId, parseSnapshot]);

  useEffect(() => {
    if (allSessions.length === 0) return;

    const interval = setInterval(() => {
      const now = Date.now();

      const updater = (prev) =>
        prev.map((s) => {
          const startedAt = s.startedAt?.getTime() || 0;
          const minutesAgo = Math.floor((now - startedAt) / 60000);
          return {
            ...s,
            minutesAgo,
            timeAgoLabel: formatTimeAgo(minutesAgo),
          };
        });

      setAllSessions(updater);
      setOtherSessions(updater);
    }, 30000);

    return () => clearInterval(interval);
  }, [allSessions.length]);

  const createSession = useCallback(
    async ({
      driverId,
      partnerId,
      driverName,
      doorNumber,
      step,
      stepLabel,
    }) => {
      const did = deviceIdRef.current;
      const email = userEmailRef.current;

      if (!did || !email) {
        console.warn(
          "[useSwapSession] createSession skipped: missing deviceId or userEmail",
          {
            deviceId: did,
            userEmail: email,
          },
        );
        return;
      }

      if (sessionCreated.current) {
        console.warn("[useSwapSession] createSession skipped: already created");
        return;
      }

      const sessionRef = buildSessionRef(did);

      if (!sessionRef) {
        console.warn("[useSwapSession] createSession skipped: ref is null");
        return;
      }

      const payload = {
        userEmail: email,
        driverId: driverId || null,
        partnerId: partnerId || null,
        driverName: driverName || driverId || null,
        step: step || "DOOR_OPENED_IN",
        stepLabel: stepLabel || "Opening door to insert battery",
        doorNumber: doorNumber ?? null,
        insertedBatteryId: null,
        takenBatteryId: null,
        startedAt: Date.now(),
        lastUpdatedAt: Date.now(),
      };

      try {
        sessionCreated.current = true;
        await set(sessionRef, payload);
        await onDisconnect(sessionRef).remove();
      } catch (error) {
        console.error("[useSwapSession] createSession failed:", error);
        sessionCreated.current = false;
      }
    },
    [sessionId, buildSessionRef],
  );

  const updateSession = useCallback(
    async (stepData) => {
      if (!sessionCreated.current) return;

      const did = deviceIdRef.current;
      if (!did) return;

      const sessionRef = buildSessionRef(did);
      if (!sessionRef) return;

      try {
        await update(sessionRef, {
          ...stepData,
          lastUpdatedAt: Date.now(),
        });
      } catch (error) {
        console.error("[useSwapSession] updateSession failed:", error);
      }
    },
    [buildSessionRef],
  );

  const removeSession = useCallback(async () => {
    if (!sessionCreated.current) return;

    const did = deviceIdRef.current;
    if (!did) return;

    const sessionRef = buildSessionRef(did);
    if (!sessionRef) return;

    try {
      await onDisconnect(sessionRef).cancel();
      await remove(sessionRef);
      sessionCreated.current = false;
    } catch (error) {
      console.error("[useSwapSession] removeSession failed:", error);
    }
  }, [buildSessionRef]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const did = deviceIdRef.current;
      if (sessionCreated.current && did) {
        const sessionRef = buildSessionRef(did);
        if (sessionRef) {
          remove(sessionRef).catch(() => {});
        }
        sessionCreated.current = false;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      const did = deviceIdRef.current;
      if (sessionCreated.current && did) {
        const sessionRef = buildSessionRef(did);
        if (sessionRef) {
          remove(sessionRef).catch(() => {});
        }
        sessionCreated.current = false;
      }
    };
  }, [buildSessionRef]);

  return {
    allSessions,
    otherSessions,
    createSession,
    updateSession,
    removeSession,
  };
}
